import type { FC } from 'react';
import { useMemo } from 'react';

import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import type { AvatarProps } from '@mui/material';
import { Avatar, Tooltip, Typography } from '@mui/material';

import ProfileFilledIcon from 'components/icons/custom/ProfileFilled';
import Colors from 'theme/colors';

/** @draft */
export interface UserAvatarProps extends AvatarProps {
  value?: string;

  status?: 'online' | 'offline' | false;
  size?: 'small' | 'medium';

  tooltip?: string | boolean;
  customSize?: string;

  /** Renders a process (not a person): gear icon on grey. */
  system?: boolean;
}

/**
 * TODO: evaluate if 'src\ui\avatar\UserAvatar.tsx' can be used instead
 * @draft
 * */
export const UserAvatar: FC<UserAvatarProps> = ({
  value,
  status = false,
  size = 'small',
  tooltip = false,
  customSize,
  system = false,
  ...other
}) => {
  const style = {
    fontSize: customSize || (size === 'small' ? '10px' : '12px'),
    lineHeight: size === 'small' ? '18px' : '20px',
    height: customSize || (size === 'small' ? '20px' : '24px'),
    width: customSize || (size === 'small' ? '20px' : '24px')
  };

  const stringToColor = useMemo(() => {
    if (value === undefined || value === null || value === '') {
      return Colors.coral[500];
    }

    const onlyFirstLettersNames = value.length === 2 || value.length === 1;

    if (onlyFirstLettersNames) {
      const endingLetter = value.split('').pop() || '';
      return (
        AVATAR_COLORS_MAPPING.find((colorForAvatar) => colorForAvatar.letters.includes(endingLetter.toUpperCase()))
          ?.colorToApply || Colors.coral[500]
      );
    }

    return Colors.coral[500];
  }, [value]);

  return (
    <Tooltip title={typeof tooltip === 'string' && tooltip !== '' ? tooltip : ''} placement='bottom-start'>
      <Avatar
        role='avatar'
        sx={(theme) => ({
          color:
            value !== undefined && value !== null && value !== ''
              ? theme.palette.common.white
              : theme.palette.text.disabled,
          border:
            status !== undefined && status
              ? `2px solid ${status === 'online' ? theme.palette.success.light : theme.palette.grey[200]}`
              : 'none',
          backgroundColor: value !== undefined && value !== null && value !== '' ? stringToColor : Colors.steel[200],
          ...(system && { color: theme.palette.text.secondary, backgroundColor: theme.palette.grey[200] }),
          fontSize: style.fontSize,
          height: style.height,
          width: style.width,
          cursor: 'pointer'
        })}
        {...other}
      >
        {system ? (
          <SettingsOutlinedIcon fontSize='small' />
        ) : value !== undefined && value !== null && value !== '' ? (
          <Typography
            sx={(theme) => ({
              fontWeight: 400,
              fontSize: style.fontSize,
              lineHeight: style.lineHeight,
              color: value === '' ? theme.palette.common.white : 'inherit'
            })}
          >
            {value}
          </Typography>
        ) : (
          <ProfileFilledIcon
            fontSize='small'
            height={style.height}
            width={style.width}
            sx={(theme) => ({
              borderRadius: '50%',
              backgroundColor: theme.palette.common.white,
              height: style.height,
              width: style.width
            })}
          />
        )}
      </Avatar>
    </Tooltip>
  );
};

const AVATAR_COLORS_MAPPING = [
  { colorToApply: Colors.coral[500], letters: ['A', 'J', 'S'] },
  { colorToApply: Colors.mango[300], letters: ['B', 'K', 'T'] },
  { colorToApply: Colors.lightblue[100], letters: ['C', 'L', 'U'] },
  { colorToApply: Colors.darkblue[100], letters: ['D', 'M', 'V'] },
  { colorToApply: Colors.purple[100], letters: ['E', 'N', 'W'] },
  { colorToApply: Colors.yellow[100], letters: ['F', 'O', 'X'] },
  { colorToApply: Colors.green[100], letters: ['G', 'P', 'Y'] },
  { colorToApply: Colors.navi[100], letters: ['H', 'Q', 'Z'] },
  { colorToApply: Colors.pink[100], letters: ['I', 'R'] }
];
